import { createContext, useContext } from 'react';
import { useAuth } from './AuthProvider';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';

interface SubscriptionTier {
    id: string;
    name: string;
    slug: string;
    platform_fee_pct: number;
    public_listing_limit: number;
    whitelabel_limit: number;
    intelligence_level: string;
    features: Record<string, boolean>;
    affiliate_commission_pct: number;
}

interface FeatureGate {
    feature_key: string;
    min_tier_slug: string;
    is_public: boolean;
}

interface SubscriptionContextType {
    tier: string;
    tierData: SubscriptionTier | null;
    tierName: string;
    isSovereign: boolean;
    isPlatinum: boolean;
    isCreator: boolean;
    platformFee: number;
    isLoading: boolean;
    canAccess: (featureKey: string) => boolean;
    getLimit: (limitKey: keyof SubscriptionTier) => number;
    allTiers: SubscriptionTier[];
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
    const { profile } = useAuth();

    // 1. Fetch all tiers
    const { data: allTiers = [], isLoading: tiersLoading } = useQuery({
        queryKey: ['subscription-tiers'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('subscription_tiers')
                .select('*')
                .order('display_order');
            if (error) throw error;
            return data as SubscriptionTier[];
        }
    });

    // 2. Fetch feature gates for dynamic enforcement
    const { data: featureGates = [], isLoading: gatesLoading } = useQuery({
        queryKey: ['feature-gates'],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('feature_gates')
                .select('feature_key, min_tier_slug, is_public');
            if (error) throw error;
            return data as FeatureGate[];
        }
    });

    const isAdmin = profile?.role === 'admin';
    const userTierSlug = isAdmin ? 'business_platinum' : (profile?.subscription_tier || 'free');
    const tierData = allTiers.find(t => t.slug === userTierSlug) || null;

    const canAccess = (featureKey: string): boolean => {
        // Admin has total access across the site
        if (profile?.role === 'admin') return true;

        // First check the tier's specific features JSON
        if (tierData?.features?.[featureKey]) return true;

        // Then check global feature gates
        const gate = featureGates.find(g => g.feature_key === featureKey);
        if (!gate) return false;
        if (!gate.is_public) return false;

        // Resolve hierarchy: search for user's tier index vs required tier index
        const tierOrder = ['free', 'creator', 'sovereign', 'business_platinum'];
        const userIndex = tierOrder.indexOf(userTierSlug);
        const requiredIndex = tierOrder.indexOf(gate.min_tier_slug);

        return userIndex >= requiredIndex;
    };

    const getLimit = (limitKey: keyof SubscriptionTier): number => {
        if (!tierData) return 0;
        const val = tierData[limitKey];
        return typeof val === 'number' ? val : 0;
    };

    // Fees: Check override first, then tier data, then global fallback
    const platformFee = profile?.platform_fee_override !== null && profile?.platform_fee_override !== undefined
        ? profile.platform_fee_override
        : (tierData?.platform_fee_pct ?? 10);

    const tierName = tierData?.name || 'Base Operator';
    const isSovereign = userTierSlug === 'sovereign';
    const isPlatinum = userTierSlug === 'business_platinum';
    const isCreator = userTierSlug === 'creator';
    const isLoading = tiersLoading || gatesLoading;

    return (
        <SubscriptionContext.Provider value={{
            tier: userTierSlug,
            tierData,
            isSovereign,
            isPlatinum,
            isCreator,
            tierName,
            platformFee,
            isLoading,
            canAccess,
            getLimit,
            allTiers
        }}>
            {children}
        </SubscriptionContext.Provider>
    );
}

export const useSubscription = () => {
    const context = useContext(SubscriptionContext);
    if (context === undefined) {
        throw new Error('useSubscription must be used within a SubscriptionProvider');
    }
    return context;
};

